import { useState } from 'react';
import { Check, CloudUpload, CreditCard, Mail, Phone, Settings, User } from 'lucide-react';
import type { Orders } from '@/apis/orders';
import { useAdminOrderDetails, useApproveOrderEta, useChildOrders } from '@/hooks/useAdminOrders';
import { RequestDocumentDialog } from '@/components/Templates/OrderDetail/RequestDocumentDialog';
import { DEFAULT_ORDER_STATUS_STYLE, ORDER_STATUS_STYLE, PAYMENT_METHOD_STYLE, PaymentIcon, STATUS_LABELS } from '@/pages/OrderManagement/order-shared';
import { formatPaymentVia } from '@/pages/OrderManagement/payment-via';
import { useAuthStore } from '@/stores/authStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { DynamicIcon } from '../Icon';

interface PaymentSystemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Orders;
}

function InfoCard({
  label,
  value,
  icon,
  ringColor,
  iconColor,
  valueColor,
  iconNode,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  ringColor?: string;
  iconColor?: string;
  valueColor?: string;
  iconNode?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-smoke px-4 py-4">
      {iconNode ?? (
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${ringColor}`}>
          <span className={iconColor}>{icon}</span>
        </div>
      )}
      <div>
        <p className="text-xs font-normal text-lightGray">{label}</p>
        <p className={`text-base font-bold leading-snug ${valueColor ?? 'text-black'}`}>{value}</p>
      </div>
    </div>
  );
}

function AddressBlock({
  label,
  dotColor,
  lines,
}: {
  label: string;
  dotColor: string;
  lines: (string | null | undefined)[];
}) {
  const filtered = lines.filter(Boolean) as string[];
  return (
    <div className="rounded-2xl border border-smoke px-4 py-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-lightGray">
        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
        {label}
      </p>
      <div className="space-y-0.5">
        {filtered.length > 0
          ? filtered.map((line, i) => (
              <p key={i} className="text-base font-normal text-black">{line}</p>
            ))
          : <p className="text-base font-normal text-lightGray">N/A</p>
        }
      </div>
    </div>
  );
}

export function PaymentSystemDialog({ open, onOpenChange, order }: PaymentSystemDialogProps) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [eta, setEta] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string>(order.id);

  const { data: childOrders = [], isLoading: isChildrenLoading } = useChildOrders(order.id, open && !order.parentOrderId);
  // List rows lack transactions and fresh metadata — load the full order the
  // same way the order-detail page does (shared query cache).
  const { data: fullOrder, isLoading: isDetailLoading } = useAdminOrderDetails(open ? selectedOrderId : '');
  const { mutate: approveOrderEta, isPending: isApproving } = useApproveOrderEta(selectedOrderId);
  const [requestDocOpen, setRequestDocOpen] = useState(false);

  const orderTabs = [order, ...childOrders];
  const selectedOrder = fullOrder ?? orderTabs.find((o) => o.id === selectedOrderId) ?? order;
  const isLoading = isChildrenLoading || (isDetailLoading && !fullOrder);

  const handleApprove = () => {
    if (!eta) return;
    approveOrderEta(
      { eta },
      { onSuccess: () => { setApproveOpen(false); setEta(''); onOpenChange(false); } }
    );
  };

  const handleClose = (val: boolean) => {
    if (!val) {
      setApproveOpen(false);
      setEta('');
      setSelectedOrderId(order.id);
    }
    onOpenChange(val);
  };

  // Derive all data from selectedOrder
  const tx = selectedOrder.transactions?.[0] ?? null;
  const txMeta = tx?.metadata ?? {};
  const txId = tx?.gatewayTransactionId || tx?.id || null;

  // AVS
  const addressLine1Check = (txMeta['address_line1_check'] ?? 'pass') as string;
  const zipCheck = (txMeta['address_zip_check'] ?? 'unchecked') as string;
  const addressLine1Passed = addressLine1Check === 'pass';
  const zipPassed = zipCheck === 'pass';
  const zipFailed = zipCheck === 'fail' || zipCheck === 'unchecked';

  // Info cards
  const rawPaymentMethod = selectedOrder.metadata?.latestPaymentMethod ?? selectedOrder.metadata?.paymentMethod ?? null;
  const paymentMethodStyle = rawPaymentMethod ? (PAYMENT_METHOD_STYLE[rawPaymentMethod.toLowerCase()] ?? null) : null;
  const paymentLabel = selectedOrder.metadata?.paidVia
    ? (formatPaymentVia(selectedOrder.metadata) ?? 'Not Set')
    : (paymentMethodStyle?.label ?? 'Not Set');
  const orderStatusStyle = ORDER_STATUS_STYLE[selectedOrder.status] ?? DEFAULT_ORDER_STATUS_STYLE;
  const statusLabel = orderStatusStyle.label ?? STATUS_LABELS[selectedOrder.status] ?? selectedOrder.status ?? 'Unknown';
  const salespersonFiles = selectedOrder.metadata?.salespersonFiles;
  const fileStatus = Array.isArray(salespersonFiles) && salespersonFiles.length > 0 ? 'Has Files' : 'No Files';
  const moderationStatus = selectedOrder.fraudStatus
    ? selectedOrder.fraudStatus.charAt(0).toUpperCase() + selectedOrder.fraudStatus.slice(1)
    : 'Not Set';

  // Addresses
  const billing = selectedOrder.billingAddress;
  const shipping = selectedOrder.shippingAddress;
  const billingLines = [billing?.addressLine1, billing?.addressLine2, billing?.city, billing?.state, billing?.postalCode, billing?.country];
  const shippingLines = [shipping?.addressLine1, shipping?.addressLine2, shipping?.city, shipping?.state, shipping?.postalCode, shipping?.country];

  // IP location
  const meta = selectedOrder.metadata as Record<string, unknown> | null;
  const ipCity = (meta?.ipCity ?? meta?.ip_city ?? null) as string | null;
  const ipRegion = (meta?.ipRegion ?? meta?.ip_region ?? null) as string | null;
  const ipCountry = (meta?.ipCountry ?? meta?.ip_country ?? null) as string | null;
  const ipAddress = selectedOrder.ipAddress;
  const ipLines = ipCity || ipRegion || ipCountry ? [ipCity, ipRegion, ipCity, ipCountry] : [ipAddress];

  // Customer
  const customer = selectedOrder.customer;

  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.userRole === 'admin';
  const isRedFlag = selectedOrder.fraudRiskLevel?.toLowerCase() === 'red';
  const needsApproval = selectedOrder.fraudStatus === 'pending' || selectedOrder.fraudStatus === 'review';
  const canApprove = isAdmin && isRedFlag && needsApproval;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-full max-w-130"
        closeButtonClassName="text-danger hover:bg-danger-light hover:text-danger rounded-full flex h-7 w-7 items-center justify-center transition-colors"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-black">Payment System</DialogTitle>
        </DialogHeader>

        <div className="h-px bg-smoke -mx-6 mb-4" />

        {/* Loading skeleton */}
        {isLoading ? (
          <div className="space-y-3 animate-pulse">
            <div className="flex gap-2">
              <div className="h-7 w-36 rounded-full bg-smoke" />
              <div className="h-7 w-28 rounded-full bg-smoke" />
            </div>
            <div className="h-10 w-full rounded-2xl bg-smoke" />
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-18 rounded-2xl bg-smoke" />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-24 rounded-2xl bg-smoke" />
              <div className="h-24 rounded-2xl bg-smoke" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-24 rounded-2xl bg-smoke" />
              <div className="h-24 rounded-2xl bg-smoke" />
            </div>
          </div>
        ) : (
          <>
        {/* AVS pills + order number tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="flex items-center gap-1 rounded-full border border-smoke px-3 py-1 text-xs font-normal text-lightGray">
            <Check size={11} className={addressLine1Passed ? 'text-success' : 'text-danger'} />
            Address_line1_check
          </span>
          <span className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-normal ${
            zipFailed ? 'border-orange-200 text-orange-500' : 'border-smoke text-lightGray'
          }`}>
            <Check size={11} className={zipPassed ? 'text-success' : 'text-orange-400'} />
            Compare ZIP
          </span>

          {/* Order number tabs — only when child orders exist */}
          {childOrders.length > 0 && (
            <div className="ml-auto flex overflow-hidden rounded-full border border-smoke">
              {orderTabs.map((o, i) => {
                const shortNum = o.orderNumber?.replace(/\D/g, '').slice(-5) || o.id.slice(-5);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSelectedOrderId(o.id)}
                    className={`px-3 py-1 text-xs font-semibold transition-colors ${
                      selectedOrderId === o.id
                        ? 'bg-primary-s500 text-neutral-white'
                        : 'bg-neutral-white text-lightGray hover:bg-smoke1'
                    } ${i > 0 && selectedOrderId !== o.id && selectedOrderId !== orderTabs[i - 1].id ? 'border-l border-smoke' : ''}`}
                  >
                    {shortNum}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Transaction ID */}
        {txId && (
          <div className="mb-4 rounded-2xl border border-smoke px-4 py-2.5">
            <p className="text-base font-normal text-lightGray">
              Transaction ID: <span className="text-primary-s500">{txId}</span>
            </p>
          </div>
        )}

        {/* 2×2 info grid */}
        <div className="grid grid-cols-2 gap-3">
          <InfoCard
            label="Status"
            value={statusLabel}
            icon={<Settings size={18} />}
            ringColor={orderStatusStyle.ringColor}
            iconColor={orderStatusStyle.iconColor}
            valueColor={orderStatusStyle.iconColor}
          />
          <InfoCard
            label="Payment"
            value={paymentLabel}
            iconNode={
              paymentMethodStyle ? (
                <PaymentIcon
                  iconName={paymentMethodStyle.iconName}
                  lucideIcon={paymentMethodStyle.lucideIcon}
                  iconColor={paymentMethodStyle.iconColor}
                  size={30}
                  wrapperClassName={`flex h-10 w-10 shrink-0 items-center justify-center border border-primary-s500 rounded-full ${paymentMethodStyle.wrapper}`}
                />
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-primary-s50 border-primary-s500">
                  <CreditCard size={18} className="text-primary-s500" />
                </div>
              )
            }
          />
          <InfoCard
            label="File Status"
            value={fileStatus}
            icon={<CloudUpload size={18} />}
            ringColor="bg-danger-light border-danger"
            iconColor="text-danger"
          />
          <InfoCard
            label="Moderation Status"
            value={moderationStatus}
            icon={<DynamicIcon name="calendar" size={18} />}
            ringColor="bg-primary-s50 border-primary-s500"
            iconColor="text-primary-s500"
          />
        </div>

        {/* Billing + Shipping addresses */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <AddressBlock label="Billing Address" dotColor="bg-primary-s500" lines={billingLines} />
          <AddressBlock label="Shipping Address" dotColor="bg-danger" lines={shippingLines} />
        </div>

        {/* IP Address + Personal Details */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <AddressBlock label="By IP Address" dotColor="bg-success" lines={ipLines} />

          <div className="rounded-2xl border border-smoke px-4 py-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-lightGray">
              <span className="h-2 w-2 rounded-full bg-primary-s500" />
              Personal Details
            </p>
            <div className="space-y-1.5">
              {customer?.fullName && (
                <div className="flex items-center gap-2">
                  <User size={13} className="shrink-0 text-silver" />
                  <span className="text-base font-normal text-black">{customer.fullName}</span>
                </div>
              )}
              {customer?.phone && (
                <div className="flex items-center gap-2">
                  <Phone size={13} className="shrink-0 text-silver" />
                  <span className="text-base font-normal text-lightGray">{customer.phone}</span>
                </div>
              )}
              {customer?.email && (
                <div className="flex items-center gap-2">
                  <Mail size={13} className="shrink-0 text-silver" />
                  <span className="truncate text-base font-normal text-lightGray">{customer.email}</span>
                </div>
              )}
              {!customer?.fullName && !customer?.phone && !customer?.email && (
                <p className="text-base font-normal text-lightGray">N/A</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions — admin only */}
        {isAdmin && (
          <div
            className="mt-4 flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {approveOpen && (
              <input
                type="date"
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                className="w-full rounded-full border border-smoke px-4 py-2.5 text-base font-normal text-black outline-none focus:border-primary-s500"
              />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setRequestDocOpen(true)}
                className="flex-1 rounded-full border border-primary-s500 py-2.5 text-base font-semibold text-primary-s500 hover:bg-primary-s50 transition-colors"
              >
                Request Documents
              </button>
              {canApprove && (
                <button
                  type="button"
                  onClick={approveOpen ? handleApprove : () => setApproveOpen(true)}
                  disabled={isApproving || (approveOpen && !eta)}
                  className="flex-1 rounded-full bg-primary-s500 py-2.5 text-base font-semibold text-neutral-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isApproving ? 'Approving...' : approveOpen ? 'Confirm Approve' : 'Approve Order'}
                </button>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </DialogContent>

      <RequestDocumentDialog
        open={requestDocOpen}
        onOpenChange={setRequestDocOpen}
        order={order}
      />
    </Dialog>
  );
}
